import { useAuth, useUser } from '@clerk/tanstack-react-start'
import { Outlet, createFileRoute, redirect, useLocation, useNavigate } from '@tanstack/react-router'
import { convexQuery } from '@convex-dev/react-query'
import { Loader, Stack, Text } from '@mantine/core'
import { useEffect, useRef } from 'react'
import { api } from 'convex/_generated/api'
import { useProfileSetup } from '@/hooks/convex/useProfile'
import { AcceptInvitationModal } from '@/features/invitations'
import { AuthHandoffProvider } from '@/features/anonymous'
import { AnchorFAB, FABProvider } from '@/components/AnchorFAB'
import { shouldInitializePostHog, usePostHog } from '@/lib/posthog'
import { PostHogSessionRecordingUpdater } from '@/components/common/PostHogSessionRecordingUpdater'

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    // Check authentication - redirect to sign-in if not authenticated
    if (!context.userId) {
      throw redirect({
        to: '/sign-in',
        search: {
          redirect_url: location.href,
        },
      })
    }

    // Ensure profile is loaded (will be created by useProfileSetup if needed)
    await context.queryClient.ensureQueryData(
      convexQuery(api.users.getCurrentProfile, {})
    )
  },
  component: AuthedLayout,
})

/**
 * Authenticated layout wrapper.
 *
 * Responsibility split (best practice):
 * - Clerk auth (session, userId, Convex JWT): TanStack Start server only.
 *   __root runs fetchClerkAuth (createServerFn + auth()) and sets context.userId.
 *   beforeLoad here only checks context.userId and redirects; no extra server call.
 * - Profile: Convex client only. useProfileSetup runs Convex
 *   queries and ensureUserExists; cannot be moved to TanStack Start.
 *
 * Flow:
 * 1. beforeLoad: redirect if !context.userId (from __root's fetchClerkAuth).
 * 2. Client: useAuth() as a safety net for session expiry; redirect if !isSignedIn.
 * 3. useProfileSetup: profile creation; stays client (Convex).
 * 4. Dashboard shows EmptyDashboardPrompt for new users with no projects/tasks.
 */
function AuthedLayout() {
  // useAuth from Clerk provides fast auth state for UI decisions
  const { isLoaded, isSignedIn, userId } = useAuth()
  const { user: clerkUser } = useUser()
  const { isPending, error, profile } = useProfileSetup()
  const navigate = useNavigate()
  const location = useLocation()
  const posthog = usePostHog()
  const mode = import.meta.env.MODE || 'development'
  const hasResolvedProfileOnce = useRef(false)

  if (profile) {
    hasResolvedProfileOnce.current = true
  }

  // PostHog identify - call once when user is authenticated
  useEffect(() => {
    const devEnabled = import.meta.env.VITE_POSTHOG_DEV_ENABLED
    if (posthog && shouldInitializePostHog(mode, devEnabled) && userId && profile) {
      try {
        const profileName = profile.firstName || profile.lastName
          ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
          : 'Unknown'

        posthog.identify(userId, {
          email: clerkUser?.primaryEmailAddress?.emailAddress,
          username: clerkUser?.username,
          name: clerkUser?.fullName,
          active_profile_id: profile._id,
          profile_name: profileName,
          is_main_profile: profile.isMain || false,
        })
      } catch {
        // PostHog may be blocked by adblocker - silently fail
      }
    }
  }, [userId, clerkUser, profile, posthog, mode])

  // Show loading while Clerk SDK is loading (fast)
  if (!isLoaded) {
    return (
      <Stack align="center" justify="center" h="100vh" gap="md">
        <Loader size="lg" />
        <Text c="dimmed">Checking authentication...</Text>
      </Stack>
    )
  }

  // Client-side safety: session expiry or desync. Redirect to sign-in
  if (!isSignedIn) {
    navigate({
      to: '/sign-in',
      search: { redirect_url: location.href },
      replace: true,
    })
    return (
      <Stack align="center" justify="center" h="100vh" gap="md">
        <Loader size="lg" />
        <Text c="dimmed">Redirecting to sign-in...</Text>
      </Stack>
    )
  }

  // Profile setup/loading gate for first authenticated resolution only.
  // After the first successful profile load, we keep rendering app content
  // during background auth/query churn so the shell doesn't regress to a
  // blocking loader mid-navigation.
  if ((isPending || !profile) && !hasResolvedProfileOnce.current) {
    return (
      <Stack align="center" justify="center" h="100vh" gap="md">
        <Loader size="lg" />
        <Text c="dimmed">Setting up your profile...</Text>
      </Stack>
    )
  }

  if (error && !hasResolvedProfileOnce.current) {
    return (
      <Stack align="center" justify="center" h="100vh" gap="md">
        <Text c="red">Error loading profile</Text>
        <Text size="sm" c="dimmed">{error instanceof Error ? error.message : 'Unknown error'}</Text>
        <Text size="xs" c="dimmed">
          Check console for details. You may need to configure Clerk JWT template for Convex.
        </Text>
      </Stack>
    )
  }

  return (
    <FABProvider>
      <PostHogSessionRecordingUpdater />
      <AcceptInvitationModal />
      <AuthHandoffProvider>
        <div data-testid="authed-ready">
          <Outlet />
        </div>
      </AuthHandoffProvider>
      <AnchorFAB />
    </FABProvider>
  )
}
